import { AgGridAngular } from '@ag-grid-community/angular';
import { ColumnState } from '@ag-grid-community/core/dist/esm/es6/columns/columnModel';
import { ValueFormatterParams } from '@ag-grid-community/core/dist/esm/es6/entities/colDef';
import { GridOptions } from '@ag-grid-community/core/dist/esm/es6/entities/gridOptions';
import { FilterChangedEvent, FirstDataRenderedEvent, GridReadyEvent, SortChangedEvent } from '@ag-grid-community/core/dist/esm/es6/events';
import { IServerSideGetRowsParams } from '@ag-grid-community/core/dist/esm/es6/interfaces/iServerSideDatasource';
import { StatusPanelDef } from '@ag-grid-community/core/dist/esm/es6/interfaces/iStatusPanel';
import { AfterViewInit, Component, DestroyRef, Inject, OnDestroy, ViewChild, computed, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { ListingTableSelectHeaderComponent } from 'listings/components/listings-table/listing-table-select-header/listing-table-select-header.component';
import { BehaviorSubject, Subscription } from 'rxjs';
import { ActionButtonsRendererComponent } from 'shared/components/ag-grid/cell-renderers/action-buttons-renderer/action-buttons-renderer.component';
import { BooleanCellRendererComponent } from 'shared/components/ag-grid/cell-renderers/boolean-cell-renderer/boolean-cell-renderer.component';
import { ClickActionRendererComponent } from 'shared/components/ag-grid/cell-renderers/click-action-renderer/click-action-renderer.component';
import { ColoredIconCellRendererComponent } from 'shared/components/ag-grid/cell-renderers/colored-icon-cell-renderer/colored-icon-cell-renderer.component';
import { ColoredPillCellRendererComponent } from 'shared/components/ag-grid/cell-renderers/colored-pill-cell-renderer/colored-pill-cell-renderer.component';
import { DistributionTrackingChannelLogoRendererComponent } from 'shared/components/ag-grid/cell-renderers/distribution-tracking-channel-logo-renderer/distribution-tracking-channel-logo-renderer.component';
import { DistributionTrackingStatusRendererComponent } from 'shared/components/ag-grid/cell-renderers/distribution-tracking-status-renderer/distribution-tracking-status-renderer.component';
import { GenericCellWithTooltipRendererComponent } from 'shared/components/ag-grid/cell-renderers/generic-cell-with-tooltip-renderer/generic-cell-with-tooltip-renderer.component';
import { GenericLogoWithNameRendererComponent } from 'shared/components/ag-grid/cell-renderers/generic-logo-with-name-renderer/generic-logo-with-name-renderer.component';
import { GenericTreeCellRendererComponent } from 'shared/components/ag-grid/cell-renderers/generic-tree-cell-renderer/generic-tree-cell-renderer.component';
import { ImageTrackerAssetCellRendererComponent } from 'shared/components/ag-grid/cell-renderers/image-tracker-asset-cell-renderer/image-tracker-asset-cell-renderer.component';
import { ImageTrackerAssetStatusRendererComponent } from 'shared/components/ag-grid/cell-renderers/image-tracker-status-renderer/image-tracker-asset-status-renderer/image-tracker-asset-status-renderer.component';
import { ImageTrackerListingStatusRendererComponent } from 'shared/components/ag-grid/cell-renderers/image-tracker-status-renderer/image-tracker-listing-status-renderer/image-tracker-listing-status-renderer.component';
import { ImportPartialSuccessStatusBarRendererComponent } from 'shared/components/ag-grid/cell-renderers/import-partial-success-status-bar-renderer/import-partial-success-status-bar-renderer.component';
import { ImportPartialSuccessStatusDisplayRendererComponent } from 'shared/components/ag-grid/cell-renderers/import-partial-success-status-display-renderer/import-partial-success-status-display-renderer.component';
import { InvitedUserLinkCellRendererComponent } from 'shared/components/ag-grid/cell-renderers/invited-user-link-renderer/invited-user-link-cell-renderer.component';
import { LinkRendererComponent } from 'shared/components/ag-grid/cell-renderers/link-rederer/link-renderer.component';
import { TranslatedTextRendererComponent } from 'shared/components/ag-grid/cell-renderers/translated-text-renderer/translated-text-renderer.component';
import { TruncatedContentTooltipCellRendererComponent } from 'shared/components/ag-grid/cell-renderers/truncated-content-tooltip-cell-renderer/truncated-content-tooltip-cell-renderer.component';
import { BooleanFloatingFilterComponent } from 'shared/components/ag-grid/filters/boolean-floating-filter/boolean-floating-filter.component';
import { DateFloatingFilterComponent } from 'shared/components/ag-grid/filters/date-floating-filter/date-floating-filter.component';
import { DateRangeFloatingFilterComponent } from 'shared/components/ag-grid/filters/date-range-floating-filter/date-range-floating-filter.component';
import { DropdownFloatingFilterComponent } from 'shared/components/ag-grid/filters/dropdown-floating-filter/dropdown-floating-filter.component';
import { RangeOptionsFloatingFilterComponent } from 'shared/components/ag-grid/filters/range-options-floating-filter/range-options-floating-filter.component';
import { ResetFiltersHeaderComponent } from 'shared/components/ag-grid/reset-filters-header/reset-filters-header.component';
import { StatusBarLogoContainerComponent } from 'shared/components/ag-grid/status-bar-logo-container/status-bar-logo-container.component';
import { GridService } from 'shared/interfaces/grid-service';
import { ListingStatusRendererComponent } from '../components/ag-grid/cell-renderers/listing-status-renderer/listing-status-renderer.component';
import { NotificationStatusRendererComponent } from '../components/ag-grid/cell-renderers/notification-status-renderer/notification-status-renderer.component';
import { CustomPaginationComponent } from '../components/ag-grid/custom-pagination/custom-pagination.component';
import { ListingStatusFloatingFilterComponent } from '../components/ag-grid/filters/listing-status-floating-filter/listing-status-floating-filter.component';
import { NotificationStatusFloatingFilterComponent } from '../components/ag-grid/filters/notification-status-floating-filter/notification-status-floating-filter.component';
import { PAGE_SIZES_LIST } from '../constants/general-constants';
import { StorageService } from '../services';
import { unsubscribeFromAll } from '../util/unsubscribeFromAll';
import { CustomLoadingCellRenderer } from './base-grid-loading-cell-renderer';
import { AiFeaturesUsageRendererComponent } from 'shared/components/ag-grid/cell-renderers/ai-features-usage-renderer/ai-features-usage-renderer.component';

@Component({
    template: '',
    standalone: false,
})
export abstract class BaseGrid implements AfterViewInit, OnDestroy {
    @ViewChild(AgGridAngular) agGrid!: AgGridAngular;
    subscriptions: { [key: string]: Subscription } = {};

    public anyRows = signal<boolean>(true);
    public rowData!: unknown[];
    public isPausedSubject$: BehaviorSubject<any> = new BehaviorSubject<any>(false);

    protected rows!: unknown[];
    protected pageNumber = signal<number>(1);
    protected pageSize = signal<number>(PAGE_SIZES_LIST[0]);
    protected totalRowCount = signal<number | undefined>(undefined);

    protected totalPages = computed(() => {
        const total = this.totalRowCount() ?? 0;
        const size = this.pageSize();
        return size > 0 ? Math.max(Math.ceil(total / size), 1) : 1;
    });

    private readonly defaultPageNumber: number = 1;

    readonly gridOptions: GridOptions = {
        rowModelType: 'serverSide',
        serverSideSortOnServer: true,
        serverSideFilterOnServer: true,
        suppressServerSideInfiniteScroll: true,
        allowDragFromColumnsToolPanel: true,
        suppressPaginationPanel: true,
        suppressCellFocus: true,
        suppressRowClickSelection: true,
        pagination: true,
        paginationPageSize: PAGE_SIZES_LIST[0],
        loadingCellRenderer: CustomLoadingCellRenderer,
        loadingCellRendererParams: { loadingMessage: 'Loading...' },
        defaultColDef: {
            sortable: true,
            resizable: true,
            filter: 'agTextColumnFilter',
            filterParams: {
                trimInput: true,
            },
            editable: false,
            suppressMenu: true,
        },
        components: {
            //Cell renderers
            actionButtonsCellRenderer: ActionButtonsRendererComponent,
            linkCellRenderer: LinkRendererComponent,
            booleanCellRenderer: BooleanCellRendererComponent,
            coloredPillCellRenderer: ColoredPillCellRendererComponent,
            coloredIconCellRenderer: ColoredIconCellRendererComponent,
            invitedUserLinkCellRenderer: InvitedUserLinkCellRendererComponent,
            listingStatusCellRenderer: ListingStatusRendererComponent,
            distributionTrackingStatusCellRenderer: DistributionTrackingStatusRendererComponent,
            distributionTrackingChannelLogoCellRenderer: DistributionTrackingChannelLogoRendererComponent,
            notificationStatusCellRenderer: NotificationStatusRendererComponent,
            truncatedContentTooltipCellRenderer: TruncatedContentTooltipCellRendererComponent,
            genericCellWithTooltipRenderer: GenericCellWithTooltipRendererComponent,
            clickActionCellRenderer: ClickActionRendererComponent,
            translatedTextRenderer: TranslatedTextRendererComponent,
            importPartialSuccessStatusBarRenderer: ImportPartialSuccessStatusBarRendererComponent,
            importPartialSuccessStatusDisplayRenderer: ImportPartialSuccessStatusDisplayRendererComponent,
            imageTrackerListingStatusRendererComponent: ImageTrackerListingStatusRendererComponent,
            imageTrackerAssetStatusRendererComponent: ImageTrackerAssetStatusRendererComponent,
            imageTrackerAssetCellRendererComponent: ImageTrackerAssetCellRendererComponent,
            genericTreeCellRendererComponent: GenericTreeCellRendererComponent,
            genericLogoWithNameRendererComponent: GenericLogoWithNameRendererComponent,
            aiFeaturesUsageCellRenderer: AiFeaturesUsageRendererComponent,

            //Floating filters
            booleanFloatingFilter: BooleanFloatingFilterComponent,
            dateFloatingFilter: DateFloatingFilterComponent,
            dateRangeFloatingFilter: DateRangeFloatingFilterComponent,
            dropdownFloatingFilter: DropdownFloatingFilterComponent,
            listingStatusFloatingFilter: ListingStatusFloatingFilterComponent,
            notificationStatusFloatingFilter: NotificationStatusFloatingFilterComponent,
            rangeOptionsFloatingFilter: RangeOptionsFloatingFilterComponent,

            //Custom headers
            resetFiltersHeader: ResetFiltersHeaderComponent,
            checkboxHeaderComponent: ListingTableSelectHeaderComponent,
        },
        rowHeight: 48,
        getRowId: (params) => params.data.id ?? (crypto as any).randomUUID(),
    };
    readonly statusBar: {
        statusPanels: StatusPanelDef[];
    } = {
        statusPanels: [
            {
                statusPanel: StatusBarLogoContainerComponent,
                key: 'logoStatusPanel',
                align: 'left',
            },
            {
                statusPanel: CustomPaginationComponent,
                key: 'paginationStatusPanel',
                statusPanelParams: {
                    pageSizeChanged: this.onPageSizeChangedHandler.bind(this),
                    pageChanged: this.onPageChangedHandler.bind(this),
                },
            },
        ],
    };

    protected constructor(
        @Inject(Object) private gridService: GridService,
        @Inject(Router) public router: Router,
        @Inject(ActivatedRoute) public activatedRoute: ActivatedRoute,
        protected destroyRef: DestroyRef,
        public storageService: StorageService = new StorageService(),
    ) {}

    public ngAfterViewInit() {
        const filterModel = this.storageService.getFromSession(`Filter${this.gridService.NAME}`) as string;
        const sortModel = this.storageService.getFromSession(`Sort${this.gridService.NAME}`) as ColumnState[];

        if (filterModel && filterModel != String.EMPTY && filterModel != JSON.stringify(this.agGrid.api.getFilterModel())) {
            this.agGrid.api.setFilterModel(filterModel);
        }

        if (sortModel && sortModel.length > 0 && sortModel != this.getSortState()) {
            this.agGrid.api.applyColumnState({
                state: sortModel,
                defaultState: { sort: null },
            });
        }

        this.activatedRoute.queryParams.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((queryParams) => {
            const pageSize = Number(queryParams.pageSize);
            const pageNumber = Number(queryParams.page);

            if (Object.keys(queryParams).length === 0) {
                this.noQueryParamsProcessing();
                this.agGrid?.api?.setGridOption('serverSideDatasource', this.createGridDataSource());
                return;
            }

            if (
                isNaN(pageSize) ||
                isNaN(pageNumber) ||
                !PAGE_SIZES_LIST.includes(pageSize) ||
                (pageNumber > 1 && this.totalRowCount() !== undefined && this.totalPages() < pageNumber)
            ) {
                this.noQueryParamsProcessing(pageSize);
                this.onPageChangedHandler(this.pageSize(), this.pageNumber());
                this.agGrid?.api?.setGridOption('serverSideDatasource', this.createGridDataSource());
                return;
            }

            if (pageSize != this.pageSize()) {
                this.pageSize.set(isNaN(pageSize) ? PAGE_SIZES_LIST[0] : Number(pageSize));
                this.onPageSizeChangedHandler(pageSize, pageNumber);
            }

            if (pageNumber != this.pageNumber()) {
                this.pageNumber.set(isNaN(Number(pageNumber)) || Number(pageNumber) < this.defaultPageNumber ? 1 : Number(pageNumber));
                this.onPageChangedHandler(this.pageSize(), this.pageNumber());
            }

            this.agGrid?.api?.setGridOption('serverSideDatasource', this.createGridDataSource());
        });
    }

    public ngOnDestroy() {
        unsubscribeFromAll(this.subscriptions);
    }

    private noQueryParamsProcessing(pageSize?: any) {
        this.pageNumber.set(this.defaultPageNumber);
        this.pageSize.set(PAGE_SIZES_LIST.includes(pageSize) ? pageSize : PAGE_SIZES_LIST[0]);
        let newUrl = `${this.router.url
            .replace(/page=.+&/, `page=${this.pageNumber()}&`)
            .replace(/pageSize=.+/, `pageSize=${this.pageSize()}`)}`;

        if (newUrl === this.router.url) {
            newUrl = newUrl.concat(`?page=${this.pageNumber()}&pageSize=${this.pageSize()}`);
        }

        window.history.replaceState(null, '', `${newUrl}`);
    }

    public createGridDataSource(): any {
        return {
            getRows: (params: IServerSideGetRowsParams) => {
                this.isPausedSubject$.next(true);
                this.subscriptions.getRows = this.gridService
                    .getRows(params, this.pageNumber(), this.pageSize())
                    .subscribe((response: any) => {
                        params.success({ rowData: response.data, rowCount: response.totalCount });
                        this.updateRowCount(response.totalCount);
                        this.rows = response.data;
                        this.setPageParams();

                        //In case the last row on the page was deleted, and the server side store is refreshed, instead of refreshing an empty page, we redirect
                        //But we check that we have a valid rowCount, meaning the call returned results
                        if (this.totalRowCount() && this.totalPages() < this.pageNumber()) {
                            //Go to actual last page
                            this.onPageChangedHandler(this.pageSize(), Math.max(this.totalPages(), this.defaultPageNumber));
                        }

                        this.isPausedSubject$.next(false);
                    });
            },
        };
    }

    public onFirstDataRendered(params: FirstDataRenderedEvent) {
        this.agGrid.api = params.api;
        // TODO: resize on window size change
        params.api.sizeColumnsToFit();
        this.setPageParams();
    }

    public onGridReady(params: GridReadyEvent<unknown>) {
        this.agGrid.api = params.api;

        if (this.anyRows() && this.totalRowCount() !== undefined) {
            this.agGrid?.api?.setGridOption('serverSideDatasource', this.createGridDataSource());
            this.agGrid.api.refreshServerSide({ purge: true });
        }
    }

    public onSortChanged(event?: SortChangedEvent) {
        const sortState = this.getSortState();

        if (JSON.stringify(sortState.toString() !== String.EMPTY)) {
            this.storageService.setInSession(`Sort${this.gridService.NAME}`, sortState);
        }
    }

    public onFilterChanged(event?: FilterChangedEvent) {
        if (JSON.stringify(this.agGrid.api.getFilterModel()) != String.EMPTY) {
            this.storageService.setInSession(`Filter${this.gridService.NAME}`, this.agGrid.api.getFilterModel());

            //If 'afterFloatingFilter' is present, the filter was triggered by the user
            //Otherwise, it was programmatically triggered by setting the filter model on the grid
            if (event?.hasOwnProperty('afterFloatingFilter')) {
                this.goToFirstPage();
            }
        }
    }

    private getSortState() {
        return this.agGrid.api
            .getColumnState()
            .filter((state) => state.sort != null)
            .map((state) => {
                return { colId: state.colId, sort: state.sort, sortIndex: state.sortIndex };
            });
    }

    public onPageSizeChangedHandler(pageSize: number, pageNumber: number) {
        this.router.navigate([], {
            relativeTo: this.activatedRoute,
            queryParams: {
                page: pageNumber,
                pageSize: pageSize,
            },
        });
        this.agGrid?.api?.setGridOption('paginationPageSize', this.pageSize());
        this.agGrid?.api?.setGridOption('serverSideDatasource', this.createGridDataSource());
    }

    public onPageChangedHandler(pageSize: number, pageNumber: number) {
        this.router.navigate([], {
            relativeTo: this.activatedRoute,
            queryParams: {
                page: pageNumber,
                pageSize: pageSize,
            },
        });

        this.pageNumber.set(pageNumber);
        this.agGrid?.api?.setGridOption('serverSideDatasource', this.createGridDataSource());
    }

    private goToFirstPage() {
        const statusBarComponent = this.agGrid.api.getStatusPanel('paginationStatusPanel') as any;
        statusBarComponent.first();
    }

    protected setPageParams() {
        const statusBarComponent = this.agGrid?.api?.getStatusPanel('paginationStatusPanel') as any;
        this.agGrid?.api?.setGridOption('paginationPageSize', this.pageSize());
        statusBarComponent?.setPageParams(this.pageNumber(), this.pageSize());
    }

    protected updateRowCount(value: number) {
        this.totalRowCount.set(value);

        if (Object.keys(this.agGrid?.api?.getFilterModel() ?? {}).length === 0) {
            this.anyRows.set(value > 0);
        }

        const statusBarComponent = this.agGrid?.api?.getStatusPanel('paginationStatusPanel') as any;
        statusBarComponent?.setRowCount(value);
    }

    public getDateFormatPattern(locale: string) {
        const getPatternForPart = (part: Intl.DateTimeFormatPart) => {
            switch (part.type) {
                case 'day':
                    return 'D'.repeat(part.value.length);
                case 'month':
                    return 'M'.repeat(part.value.length);
                case 'year':
                    return 'Y'.repeat(part.value.length);
                case 'literal':
                    return part.value;
                default:
                    console.log('Unsupported date part', part);
                    return '';
            }
        };

        return new Intl.DateTimeFormat(locale).formatToParts(new Date('1987-03-06')).map(getPatternForPart).join('');
    }

    protected dateFormatter(params: ValueFormatterParams) {
        if (!params.value) {
            return '-';
        }

        return new Date(params.value).toLocaleString();
    }

    protected unixDateFormatter(params: ValueFormatterParams) {
        if (!params.value) {
            return '-';
        }
        const date = new Date(params.value * 1000);
        return date.toLocaleString();
    }

    protected updateSingleCell(rowId: string, columnKey: string, newValue: any) {
        const rowNode = this.agGrid?.api.getRowNode(rowId);

        if (rowNode) {
            rowNode.setDataValue(columnKey, newValue);

            this.agGrid?.api.refreshCells({
                rowNodes: [rowNode],
                force: true,
            });
        }
    }

    protected updateNonColumnDataById(rowId: string, dataField: string, newValue: any) {
        const rowNode = this.agGrid?.api.getRowNode(rowId);

        if (rowNode) {
            rowNode.data[dataField] = newValue;

            this.agGrid?.api.refreshCells({
                rowNodes: [rowNode],
                force: true,
            });
        }
    }
}
